/**
 * Input Validation & Sanitization Utilities
 * Following OWASP Input Validation Best Practices
 * https://cheatsheetseries.owasp.org/cheatsheets/Input_Validation_Cheat_Sheet.html
 */

// =============================================================================
// VALIDATION SCHEMAS
// =============================================================================

/**
 * Validation schema for customer data
 */
export const customerSchema = {
  name: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s'-]+$/,
    errorMessage: 'Name must be 2-100 characters and contain only letters, numbers, spaces, hyphens, and apostrophes'
  },
  email: {
    type: 'string',
    required: false,
    maxLength: 254,
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    errorMessage: 'Invalid email format'
  },
  phone: {
    type: 'string',
    required: false,
    maxLength: 20,
    pattern: /^\+?[0-9\s()-]{7,20}$/,
    errorMessage: 'Phone must be 7-20 characters and contain only numbers, spaces, +, -, (, )'
  },
  status: {
    type: 'string',
    required: true,
    enum: ['Active', 'Inactive'],
    errorMessage: 'Status must be either Active or Inactive'
  }
};

/**
 * Validation schema for staff data
 */
export const staffSchema = {
  name: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s'-]+$/,
    errorMessage: 'Name must be 2-100 characters and contain only letters, spaces, hyphens, and apostrophes'
  },
  role: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]+$/,
    errorMessage: 'Role must be 2-50 characters and contain only letters and spaces'
  },
  email: {
    type: 'string',
    required: true,
    maxLength: 254,
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    errorMessage: 'Invalid email format'
  },
  phone: {
    type: 'string',
    required: false,
    maxLength: 20,
    pattern: /^\+?[0-9\s()-]{7,20}$/,
    errorMessage: 'Phone must be 7-20 characters and contain only numbers, spaces, +, -, (, )'
  },
  status: {
    type: 'string',
    required: true,
    enum: ['Active', 'On Leave'],
    errorMessage: 'Status must be either Active or On Leave'
  },
  joinDate: {
    type: 'string',
    required: false,
    pattern: /^\d{4}-\d{2}-\d{2}$/,
    errorMessage: 'Join date must be in YYYY-MM-DD format'
  }
};

/**
 * Validation schema for inventory items
 */
export const inventorySchema = {
  name: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 200,
    pattern: /^[a-zA-Z0-9\s\-_.,()&]+$/,
    errorMessage: 'Name must be 2-200 characters and contain only alphanumeric characters and basic punctuation'
  },
  category: {
    type: 'string',
    required: false,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]+$/,
    errorMessage: 'Category must be max 50 characters and contain only letters and spaces'
  },
  costPrice: {
    type: 'number',
    required: true,
    min: 0,
    max: 999999999.99,
    errorMessage: 'Cost price must be a positive number less than 1 billion'
  },
  sellingPrice: {
    type: 'number',
    required: true,
    min: 0,
    max: 999999999.99,
    errorMessage: 'Selling price must be a positive number less than 1 billion'
  },
  stock: {
    type: 'number',
    required: false,
    min: 0,
    max: 999999999,
    integer: true,
    errorMessage: 'Stock must be a positive integer less than 1 billion'
  }
};

/**
 * Validation schema for billing data
 */
export const billingSchema = {
  customer: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s'-]+$/,
    errorMessage: 'Customer name must be 2-100 characters and contain only letters, numbers, spaces, hyphens, and apostrophes'
  },
  date: {
    type: 'string',
    required: false,
    pattern: /^\d{4}-\d{2}-\d{2}$/,
    errorMessage: 'Date must be in YYYY-MM-DD format'
  },
  status: {
    type: 'string',
    required: true,
    enum: ['Pending', 'Paid', 'Overdue'],
    errorMessage: 'Status must be Pending, Paid, or Overdue'
  }
};

/**
 * Validation schema for billing settings
 */
export const billingSettingsSchema = {
  companyName: {
    type: 'string',
    required: true,
    minLength: 2,
    maxLength: 200,
    pattern: /^[a-zA-Z0-9\s\-_.,()&]+$/,
    errorMessage: 'Company name must be 2-200 characters'
  },
  companyAddress: {
    type: 'string',
    required: false,
    maxLength: 500,
    errorMessage: 'Company address must be max 500 characters'
  },
  companyPhone: {
    type: 'string',
    required: false,
    maxLength: 20,
    pattern: /^\+?[0-9\s()-]{7,20}$/,
    errorMessage: 'Phone must be 7-20 characters'
  },
  companyEmail: {
    type: 'string',
    required: false,
    maxLength: 254,
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    errorMessage: 'Invalid email format'
  },
  taxRate: {
    type: 'string',
    required: false,
    pattern: /^\d+(\.\d{1,2})?$/,
    errorMessage: 'Tax rate must be a valid number'
  },
  currency: {
    type: 'string',
    required: false,
    enum: ['LKR', 'USD', 'EUR', 'GBP', 'INR'],
    errorMessage: 'Invalid currency'
  },
  invoicePrefix: {
    type: 'string',
    required: false,
    maxLength: 10,
    pattern: /^[A-Z0-9-]+$/,
    errorMessage: 'Invoice prefix must be max 10 uppercase letters, numbers, or hyphens'
  },
  termsText: {
    type: 'string',
    required: false,
    maxLength: 2000,
    errorMessage: 'Terms text must be max 2000 characters'
  },
  paymentInstructions: {
    type: 'string',
    required: false,
    maxLength: 1000,
    errorMessage: 'Payment instructions must be max 1000 characters'
  }
};

// =============================================================================
// VALIDATION FUNCTIONS
// =============================================================================

/**
 * Sanitize string input by removing potentially dangerous characters
 * Prevents XSS attacks by escaping HTML entities
 * 
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  // Escape HTML entities to prevent XSS
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
  
  return sanitized;
};

/**
 * Validate a single field against its schema
 * 
 * @param {any} value - The value to validate
 * @param {object} fieldSchema - The schema definition for the field
 * @param {string} fieldName - The name of the field (for error messages)
 * @returns {object} - { valid: boolean, error: string|null }
 */
export const validateField = (value, fieldSchema, fieldName) => {
  // Check required
  if (fieldSchema.required && (value === null || value === undefined || value === '')) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  // Skip further validation if not required and empty
  if (!fieldSchema.required && (value === null || value === undefined || value === '')) {
    return { valid: true, error: null };
  }
  
  // Type validation
  if (fieldSchema.type === 'string') {
    if (typeof value !== 'string') {
      return { valid: false, error: `${fieldName} must be a string` };
    }
    
    // Length validation
    if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} is too short` };
    }
    if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} is too long` };
    }
    
    // Pattern validation
    if (fieldSchema.pattern && !fieldSchema.pattern.test(value)) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} format is invalid` };
    }
    
    // Enum validation
    if (fieldSchema.enum && !fieldSchema.enum.includes(value)) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} has invalid value` };
    }
  }
  
  if (fieldSchema.type === 'number') {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) {
      return { valid: false, error: `${fieldName} must be a valid number` };
    }
    
    // Integer validation
    if (fieldSchema.integer && !Number.isInteger(numValue)) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} must be an integer` };
    }
    
    // Range validation
    if (fieldSchema.min !== undefined && numValue < fieldSchema.min) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} is too small` };
    }
    if (fieldSchema.max !== undefined && numValue > fieldSchema.max) {
      return { valid: false, error: fieldSchema.errorMessage || `${fieldName} is too large` };
    }
  }
  
  if (fieldSchema.type === 'boolean') {
    if (typeof value !== 'boolean') {
      return { valid: false, error: `${fieldName} must be a boolean` };
    }
  }
  
  return { valid: true, error: null };
};

/**
 * Validate an entire object against a schema
 * Rejects unexpected fields to prevent mass assignment vulnerabilities
 * 
 * @param {object} data - The data object to validate
 * @param {object} schema - The validation schema
 * @returns {object} - { valid: boolean, errors: array, sanitizedData: object }
 */
export const validateData = (data, schema) => {
  const errors = [];
  const sanitizedData = {};
  
  // Check for unexpected fields (OWASP: Reject unexpected input)
  const allowedFields = Object.keys(schema);
  const providedFields = Object.keys(data);
  const unexpectedFields = providedFields.filter(field => !allowedFields.includes(field));
  
  if (unexpectedFields.length > 0) {
    errors.push(`Unexpected fields: ${unexpectedFields.join(', ')}`);
  }
  
  // Validate each field in the schema
  for (const [fieldName, fieldSchema] of Object.entries(schema)) {
    const value = data[fieldName];
    const validation = validateField(value, fieldSchema, fieldName);
    
    if (!validation.valid) {
      errors.push(validation.error);
    } else if (value !== null && value !== undefined && value !== '') {
      // Sanitize and add to sanitized data
      if (fieldSchema.type === 'string') {
        sanitizedData[fieldName] = sanitizeString(value);
      } else if (fieldSchema.type === 'number') {
        sanitizedData[fieldName] = typeof value === 'string' ? parseFloat(value) : value;
      } else {
        sanitizedData[fieldName] = value;
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    sanitizedData
  };
};

/**
 * Validate search query input
 * Prevents SQL injection and NoSQL injection attacks
 * 
 * @param {string} query - The search query
 * @returns {object} - { valid: boolean, sanitized: string }
 */
export const validateSearchQuery = (query) => {
  if (typeof query !== 'string') {
    return { valid: false, sanitized: '' };
  }
  
  // Max length check
  if (query.length > 200) {
    return { valid: false, sanitized: '' };
  }
  
  // Remove dangerous characters that could be used for injection
  const sanitized = query
    .replace(/[<>'"`;\\]/g, '') // Remove potential script/SQL injection chars
    .replace(/\$/g, '') // Remove MongoDB operators
    .trim();
  
  return { valid: true, sanitized };
};

/**
 * Validate ID format
 * Prevents injection attacks through ID parameters
 * 
 * @param {any} id - The ID to validate
 * @param {string} format - Expected format ('number', 'string', 'uuid')
 * @returns {boolean}
 */
export const validateId = (id, format = 'number') => {
  if (format === 'number') {
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    return Number.isInteger(numId) && numId > 0 && numId < 999999999;
  }
  
  if (format === 'string') {
    return typeof id === 'string' && /^[A-Z]+-\d{3,6}$/.test(id);
  }
  
  if (format === 'uuid') {
    return typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  }
  
  return false;
};

// =============================================================================
// EXPORT ALL SCHEMAS
// =============================================================================

export const schemas = {
  customer: customerSchema,
  staff: staffSchema,
  inventory: inventorySchema,
  billing: billingSchema,
  billingSettings: billingSettingsSchema
};
